import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@/components/ui";
import { MatterLostWageContextMenu } from "@/components/features/matter/context-menus/MatterLostWageContextMenu";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import { PageHighlightCards } from "@/components/ui/PageHighlightsCards/PageHighlightCards";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal, useHandleSearchParams } from "@/hooks/useGlobalModal";
import {
  useDeleteMatterLostWage,
  useUpdateMatterLostWage,
} from "@/lib/data/react-query/matters/matter-lost-wage";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MatterLostWage } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatDate } from "@/lib/utils/format";
import { LostWageIcon } from "@/lib/utils/iconUtils";
import { calculateLostWages } from "@/lib/utils/math";
import { Pencil, PlusCircle } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

function lostWagePositionLabel(w: MatterLostWage) {
  const employed = w.employed ?? true;
  if (!employed) return "Not Employed";
  return w.positionTitle?.trim() || "—";
}
interface MatterLostWagesProps {
  matter: Matter;
  searchLostWageId?: string;
  showHighlights?: boolean;
  className?: string;
  createButtonClassName?: string;
  showTitle?: boolean;
  titleText?: string;
}

export default function MatterLostWages({
  matter,
  searchLostWageId,
  showHighlights = true,
  className,
  createButtonClassName,
  showTitle = false,
  titleText = "Employment and lost wages",
}: MatterLostWagesProps) {
  const { hasActionPermission } = useUser();
  const { openLostWageModal } = useGlobalModal();
  const deleteLostWage = useDeleteMatterLostWage();
  const updateLostWage = useUpdateMatterLostWage();
  const { handleLostWageSearch } = useHandleSearchParams();
  const lostWages = useMemo(() => matter.lostWages, [matter]);
  const hasViewPermission = Boolean(true);
  const processedSearches = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!searchLostWageId) return;

    const searchKey = `lost-wage-${searchLostWageId}`;
    if (processedSearches.current.has(searchKey)) {
      return;
    }
    processedSearches.current.add(searchKey);

    handleLostWageSearch(matter, searchLostWageId);
  }, [handleLostWageSearch, matter, searchLostWageId]);

  const pageHighlightCards = useMemo(() => {
    return [
      {
        title: "Jobs affected",
        value: lostWages.length,
      },
      {
        title: "Cumulative days missed",
        value: lostWages.reduce(
          (acc, lostWage) => acc + (lostWage.timeMissed ?? 0),
          0,
        ),
      },
      {
        title: "Total lost wages (est.)",
        value: lostWages.reduce(
          (acc, lostWage) =>
            acc +
            calculateLostWages(
              lostWage.positionType,
              lostWage.positionRate,
              lostWage.timeMissed,
            ),
          0,
        ),
        isCurrency: true,
      },
    ];
  }, [lostWages]);

  const sortedLostWages = useMemo(() => {
    return [...lostWages].sort((a, b) => {
      const aTime = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
      const bTime = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
      return bTime - aTime;
    });
  }, [lostWages]);

  const canCreateLostWages = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLostWages,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDeleteLostWages = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLostWages,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canUpdateLostWages = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLostWages,
    ACTION_PERMISSION_VALUES.update,
  );

  const captureButton = canCreateLostWages ? (
    <Button
      size="sm"
      variant="outline"
      onClick={() => {
        openLostWageModal({
          matterId: matter.id,
          editing: false,
          initialTab: "details",
        });
      }}
      className={cn("bg-background", createButtonClassName)}
    >
      <PlusCircle className="h-4 w-4 shrink-0" />
      Add employment
    </Button>
  ) : null;

  return (
    <div
      className={cn(
        showTitle ? "space-y-3" : "flex flex-col gap-y-3 p-5",
        className,
      )}
    >
      {showTitle && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Text
            variant="body"
            className="flex items-center gap-2 font-semibold"
          >
            <LostWageIcon className="h-4 w-4" />
            {titleText}
          </Text>
          {captureButton}
        </div>
      )}

      {hasViewPermission && showHighlights && (
        <PageHighlightCards items={pageHighlightCards} />
      )}

      {!showTitle && canCreateLostWages && (
        <div className="flex w-full flex-1 grow flex-wrap items-center gap-x-2">
          <div className="flex grow" />
          {captureButton}
        </div>
      )}

      {!hasViewPermission && (
        <div className="rounded-2xl border border-dashed p-5">
          <Text variant="subHeading" color="muted">
            You do not have permission to view lost wages.
          </Text>
        </div>
      )}

      {hasViewPermission && sortedLostWages.length === 0 && (
        <div className="rounded-2xl border border-dashed p-5">
          <Text variant="subHeading" color="muted">
            You haven&apos;t added employment wages yet.
            {canCreateLostWages
              ? " Use Add employment to create the first entry."
              : ""}
          </Text>
        </div>
      )}

      {hasViewPermission && sortedLostWages.length > 0 && (
        <div className="overflow-hidden rounded-2xl border">
          <Table>
            <TableHeader className="bg-muted/35">
              <TableRow className="hover:bg-muted/35">
                <TableHead className="w-[11%]">Date</TableHead>
                <TableHead className="w-[18%]">Position</TableHead>
                <TableHead className="w-[14%]">Type</TableHead>
                <TableHead className="w-[11%]">Rate</TableHead>
                <TableHead className="w-[9%]">Days missed</TableHead>
                <TableHead className="w-[12%]">Est. lost wages</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-14" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedLostWages.map((lostWage) => {
                const lostWageId = lostWage.id;
                const employed = lostWage.employed;
                const est = employed
                  ? calculateLostWages(
                      lostWage.positionType,
                      lostWage.positionRate,
                      lostWage.timeMissed,
                    )
                  : null;
                const description = lostWage.description?.trim() ?? "";
                const lostWageLabel = lostWagePositionLabel(lostWage);
                const handleEdit = () => {
                  if (!lostWageId) return;
                  openLostWageModal({
                    matterId: matter.id,
                    lostWageId,
                    editing: true,
                    initialTab: "details",
                  });
                };
                return (
                  <MatterLostWageContextMenu
                    canDelete={canDeleteLostWages && Boolean(lostWageId)}
                    canUpdate={canUpdateLostWages && Boolean(lostWageId)}
                    key={lostWageId}
                    employed={lostWage.employed}
                    id={lostWageId}
                    isDeleting={deleteLostWage.isPending}
                    isUpdating={updateLostWage.isPending}
                    label={lostWageLabel}
                    matter={matter}
                    onEdit={handleEdit}
                    onDelete={async () => {
                      await deleteLostWage.mutateAsync({
                        matterId: matter.id,
                        id: lostWageId,
                      });
                    }}
                    onEmployedChange={async (employed) => {
                      await updateLostWage.mutateAsync({
                        dateModified: lostWage.dateModified,
                        employed,
                        id: lostWageId,
                        matterId: matter.id,
                      });
                    }}
                    positionRate={lostWage.positionRate}
                    positionType={lostWage.positionType}
                    trigger={
                      <TableRow
                        key={lostWageId}
                        className="cursor-pointer"
                        onClick={() => {
                          if (!lostWageId) return;
                          handleEdit();
                        }}
                      >
                        <TableCell>
                          {formatDate(
                            convertToLocalFromDatabaseDateTime(
                              lostWage.dateCreated,
                            ),
                          )}
                        </TableCell>
                        <TableCell>
                          <Text variant="body" className="font-semibold">
                            {lostWagePositionLabel(lostWage)}
                          </Text>
                        </TableCell>
                        <TableCell>
                          {employed
                            ? lostWage.positionType?.trim() || "—"
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {employed && lostWage.positionRate != null ? (
                            <CurrencyWrapper value={lostWage.positionRate} />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {employed && lostWage.timeMissed != null
                            ? String(lostWage.timeMissed)
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {est != null ? <CurrencyWrapper value={est} /> : "—"}
                        </TableCell>
                        <TableCell>
                          {description ? (
                            <div
                              className="max-w-[200px] truncate lg:max-w-[280px]"
                              title={description}
                            >
                              {description}
                            </div>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            data-testid="edit-lost-wage-button"
                            disabled={!lostWageId}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!lostWageId) return;
                              handleEdit();
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    }
                  />
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
